/**
 * BrandProductSection Component
 * Displays a product carousel for a specific brand on the homepage
 * Mirrors CategoryProductSection but organized by brand
 */

import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { imageHelpers } from '@/services/api.js';
import ProductCarousel from './ProductCarousel.jsx';
import { BRAND_PAGE_PRODUCT_SORT } from '../../constants/productSort.js';

// Simple intersection observer hook with stable options
const useIntersectionObserver = (ref, options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '200px',
      ...options
    };

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, observerOptions);

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [ref]);

  return isIntersecting;
};

const BrandProductSection = React.memo(({ brand, index }) => {
  const sectionRef = useRef(null);
  const isVisible = useIntersectionObserver(sectionRef);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  // Once visible, keep it loaded
  useEffect(() => {
    if (isVisible && !hasBeenVisible) {
      setHasBeenVisible(true);
    }
  }, [isVisible, hasBeenVisible]);

  // Load first few sections immediately for better UX
  const shouldLoad = hasBeenVisible || index < 2;

  const brandImageUrls = imageHelpers.getBrandImageUrl(brand);
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);

  const handleImageError = () => {
    if (currentUrlIndex < brandImageUrls.length - 1) {
      setCurrentUrlIndex(currentUrlIndex + 1);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="category-product-section rounded-0 p-0"
      data-brand-id={brand.id}
      data-brand-name={brand.name}
    >
      <div className="category-product-section__header p-3 bg-stash-primary-darker">
        <div className="category-product-section__title-area">
          <div className="d-flex align-items-center gap-3">
            {brandImageUrls && brandImageUrls.length > 0 && (
              <img
                src={brandImageUrls[currentUrlIndex]}
                alt={`${brand.name} logo`}
                className="brand-section-logo"
                onError={handleImageError}
                loading="lazy"
                style={{
                  height: '36px',
                  width: 'auto',
                  maxWidth: '120px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  padding: '4px 8px'
                }}
              />
            )}
            <h2 className="category-product-section__title text-white">
              {brand.name}
            </h2>
          </div>
          <Link
            to={`/brands/${brand.slug}`}
            className="category-product-section__view-all text-white"
          >
            View All
            <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        </div>
      </div>

      <div className="category-product-section__content">
        {shouldLoad ? (
          <MemoizedProductCarousel
            brandId={brand.id}
            categoryName={brand.name}
            sortBy={BRAND_PAGE_PRODUCT_SORT}
          />
        ) : (
          <div className="category-product-section__placeholder">
            <div className="category-product-section__skeleton">
              <div className="row">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="col-xl-3 col-lg-4 col-md-6 mb-3">
                    <div className="card border rounded-3">
                      <div className="placeholder bg-light rounded-top" style={{ height: '200px' }}></div>
                      <div className="card-body">
                        <div className="placeholder bg-light rounded mb-2" style={{ height: '20px' }}></div>
                        <div className="placeholder bg-light rounded" style={{ height: '16px', width: '60%' }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.brand.id === nextProps.brand.id &&
    prevProps.brand.name === nextProps.brand.name &&
    prevProps.brand.slug === nextProps.brand.slug &&
    prevProps.index === nextProps.index
  );
});

// Memoized ProductCarousel to prevent unnecessary re-renders
const MemoizedProductCarousel = React.memo(({ brandId, categoryName, sortBy }) => (
  <ProductCarousel
    brandId={brandId}
    categoryName={categoryName}
    sortBy={sortBy}
  />
), (prevProps, nextProps) => {
  return (
    prevProps.brandId === nextProps.brandId &&
    prevProps.categoryName === nextProps.categoryName &&
    prevProps.sortBy === nextProps.sortBy
  );
});

BrandProductSection.displayName = 'BrandProductSection';
MemoizedProductCarousel.displayName = 'MemoizedBrandProductCarousel';

export default BrandProductSection;